import type { ParsedGPX, Point } from "@we-gold/gpxjs";
import type { ServerControlPoint } from "./api-hiking";
import { inside4326Extent } from "./proj";
import { findOptimalVertexIndex } from "./utils";

/*
 * Get line coordinates from gpx file
 */
export function getGpxCoordinates(parsedGPX: ParsedGPX): number[][] {
  const coordinates: number[][] = [];
  let points: Point[];

  if (parsedGPX.tracks.length > 0) {
    points = parsedGPX.tracks[0].points;
  } else if (parsedGPX.routes.length > 0) {
    points = parsedGPX.routes[0].points;
  } else throw new Error("Could not find any tracks or routes in the gpx file.");

  for (const point of points) {
    if (!inside4326Extent([point.longitude, point.latitude])) {
      throw new Error("Coordinates must be inside swiss extent (EPSG 4326)");
    }
    coordinates.push([point.longitude, point.latitude]);
  }
  return coordinates;
}

/*
 * Creates control points snapped to the line from gpx file
 */
export function getGpxControlPoints(parsedGPX: ParsedGPX, coordinates: number[][]): ServerControlPoint[] {
  const controlPointsWithStopovers: ServerControlPoint[] = [];

  // Subarray of coordinates from the last control point found
  let remainingCoordinates = coordinates;
  let remainingWindowIndex = 0;

  for (const cp of parsedGPX.waypoints) {
    const optimal_vertex_index =
      remainingWindowIndex + findOptimalVertexIndex(remainingCoordinates, [cp.longitude, cp.latitude]);

    // Since we later use these indexes to break line into segments, the condition avoids creating segments with only one point
    // in the cases where the optimal vertex is just one index ahead of the previous one
    remainingWindowIndex = optimal_vertex_index + (optimal_vertex_index === remainingWindowIndex ? 2 : 1);

    remainingCoordinates = coordinates.slice(remainingWindowIndex, coordinates.length);
    controlPointsWithStopovers.push({
      vertex_index: optimal_vertex_index,
      // snaps to line
      coordinates: coordinates[optimal_vertex_index],
      name: cp.name || null,
      features: [],
      distance: Number.NaN,
      duration: Number.NaN,
    });
  }

  // Create start and end control points, if they are not already in the list
  if (!controlPointsWithStopovers[0] || controlPointsWithStopovers[0].vertex_index !== 0) {
    controlPointsWithStopovers.unshift({
      vertex_index: 0,
      coordinates: coordinates[0],
      name: "Start point",
      features: [],
      distance: Number.NaN,
      duration: Number.NaN,
    });
  }

  if (
    controlPointsWithStopovers.at(-1)?.vertex_index !== coordinates.length - 1 ||
    controlPointsWithStopovers.length === 1
  ) {
    controlPointsWithStopovers.push({
      vertex_index: coordinates.length - 1,
      coordinates: coordinates[coordinates.length - 1],
      name: "End point",
      features: [],
      distance: Number.NaN,
      duration: Number.NaN,
    });
  }

  return controlPointsWithStopovers;
}
